import React, { useEffect, useRef, useState } from 'react';

// Barrierefreier Modal-Rahmen: role=dialog, Fokus-Falle, Escape schließt,
// Fokus kehrt beim Schließen zum auslösenden Element zurück.
export default function Modal({ onClose, children, style, label }) {
  const ref = useRef(null);
  const overlayRef = useRef(null);
  // Nur schließen, wenn Maus-Down UND Klick auf dem Hintergrund lagen — sonst schließt
  // eine Textauswahl, die im Dialog beginnt und auf dem Hintergrund endet, das Modal
  const downOnOverlay = useRef(false);
  // Schon beim Rendern merken — im Effekt hätte ein autoFocus-Kind den Fokus bereits
  const [previouslyFocused] = useState(() => document.activeElement);
  // onClose in einem Ref halten: der Mount-Effekt darf bei Re-Renders des
  // Elternteils (z.B. Standort-Polling) nicht erneut laufen und den Fokus stehlen
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });

  useEffect(() => {
    // autoFocus-Kinder (z.B. Bestätigen-Button, Passwortfeld) nicht überstimmen
    if (ref.current && !ref.current.contains(document.activeElement)) ref.current.focus();

    // Bei gestapelten Modals (z.B. Bestätigungsdialog über Detail-Modal)
    // darf nur das oberste auf Escape/Tab reagieren
    const isTopmost = () => {
      const overlays = document.querySelectorAll('.modal-overlay');
      return overlays[overlays.length - 1] === overlayRef.current;
    };

    const handleKey = e => {
      if (!isTopmost()) return;
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !ref.current) return;
      const focusables = ref.current.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !ref.current.contains(document.activeElement))) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKey, true);
    return () => {
      document.removeEventListener('keydown', handleKey, true);
      previouslyFocused?.focus?.();
    };
  }, [previouslyFocused]);

  return (
    <div
      className="modal-overlay"
      ref={overlayRef}
      onMouseDown={e => { downOnOverlay.current = e.target === e.currentTarget; }}
      onClick={e => {
        if (downOnOverlay.current && e.target === e.currentTarget) onClose();
        downOnOverlay.current = false;
      }}
    >
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        ref={ref}
        style={style}
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

export function ModalCloseButton({ onClose }) {
  return (
    <button
      type="button"
      className="btn btn-secondary"
      style={{ padding: '6px 10px', minHeight: 44, minWidth: 44 }}
      onClick={onClose}
      aria-label="Schließen"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </button>
  );
}
